import { useMemo } from 'react';
import type { ColumnDef, FunnelMapping, VisualizationConfig } from '@deckspace/types';
import { prepFunnelSeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: FunnelMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

interface FunnelStage {
  label: string;
  value: number;
  widthPct: number;
  opacity: number;
}

export function FunnelChart({ rows, columnDefs, mapping }: Props) {
  const stages = useMemo((): FunnelStage[] => {
    if (rows.length === 0) return [];
    const { labels, values } = prepFunnelSeries(rows, columnDefs, mapping);
    if (labels.length === 0) return [];
    const maxValue = values[0] ?? 1;
    return labels.map((label, i) => ({
      label,
      value: values[i] ?? 0,
      widthPct: Math.max(10, ((values[i] ?? 0) / maxValue) * 100),
      opacity: Math.max(0.25, 1 - i * 0.15),
    }));
  }, [rows, columnDefs, mapping]);

  if (stages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No data available for funnel chart
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 py-2 h-full justify-center">
      {stages.map((stage, i) => (
        <div key={i} className="flex flex-col items-center" style={{ width: `${stage.widthPct}%` }}>
          <div
            className="w-full rounded bg-blue-500 px-3 py-1.5 text-center text-xs text-white"
            style={{ opacity: stage.opacity }}
          >
            {stage.label} — {stage.value}
          </div>
        </div>
      ))}
    </div>
  );
}
